import type { BusinessDefinitionNeededDisplay } from "@ando/core/domain";
import { HighlightNote } from "@ando/ui";
import { Trans } from "@lingui/react/macro";
import { MessageCircleQuestion } from "lucide-react";
import * as React from "react";

import { useSendQuery } from "../hooks/useSendQuery";

export interface MessageBusinessDefinitionProps {
  readonly payload: BusinessDefinitionNeededDisplay;
  readonly selectedAnswer?: string;
}

/** The user defines the business rule; the server verifies and saves it. */
export const MessageBusinessDefinition: React.FC<MessageBusinessDefinitionProps> = ({
  payload,
  selectedAnswer,
}) => {
  const { send, pending } = useSendQuery();
  const [answer, setAnswer] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [custom, setCustom] = React.useState(!payload.measured_value_label);
  const sent = Boolean(selectedAnswer) || sending;
  const fieldId = React.useId();

  const submitAnswer = async (text: string) => {
    if (!text || sent || pending) return;
    setSending(true);
    setError(false);
    try {
      await send({
        text,
        businessDefinitionToken: payload.definition_token,
        configOverride: {
          focus: "structured",
          ...(payload.connection_id
            ? { selectedConnectionIds: [payload.connection_id] }
            : {}),
        },
      });
    } catch {
      setSending(false);
      setError(true);
    }
  };
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitAnswer(answer.trim());
  };

  return (
    <HighlightNote
      tone="accent"
      icon={<MessageCircleQuestion size={12} strokeWidth={1.7} aria-hidden />}
      label={<Trans>Definisci il criterio</Trans>}
      className="w-full"
    >
      <p className="text-[13px] text-ink-2 leading-snug">{payload.question}</p>
      {selectedAnswer ? (
        <p className="mt-3 border-hairline border-t pt-3 text-[13px] text-ink-2" role="status">
          <Trans>Criterio inviato:</Trans> {selectedAnswer}
        </p>
      ) : (
        <div className="mt-3 space-y-2.5">
          {payload.measured_value_label ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={sent || pending}
                onClick={() => void submitAnswer(`Per questa analisi, usa «${payload.measured_value_label}» come definizione della misura richiesta.`)}
                className="rounded-md border border-ando-orange px-3 py-1.5 text-[12px] font-medium text-ink-1 hover:bg-ando-orange/5 disabled:opacity-50"
              >
                <Trans>Usa «{payload.measured_value_label}»</Trans>
              </button>
              <button type="button" disabled={sent || pending} onClick={() => setCustom(true)} className="rounded-md border border-hairline px-3 py-1.5 text-[12px] text-ink-2 hover:bg-ink-1/5 disabled:opacity-50">
                <Trans>Indico un altro criterio</Trans>
              </button>
            </div>
          ) : null}
          {custom ? <form onSubmit={submit} className="space-y-2.5">
          <label htmlFor={fieldId} className="block text-[12px] text-ink-3">
            <Trans>Come definite questo dato nella vostra azienda?</Trans>
          </label>
          <textarea
            id={fieldId}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            disabled={sent || pending}
            rows={3}
            maxLength={2000}
            className="w-full resize-y rounded-md border border-hairline bg-transparent px-3 py-2 text-[13px] text-ink-1 outline-none focus-visible:border-ando-orange focus-visible:ring-2 focus-visible:ring-ando-orange/30 disabled:opacity-60"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!answer.trim() || sent || pending}
              className="rounded-md border border-ando-orange px-3 py-1.5 text-[12px] font-medium text-ink-1 hover:bg-ando-orange/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ando-orange disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trans>Usa questa definizione</Trans>
            </button>
          </div>
          </form> : null}
          {error ? <p role="alert" className="text-[12px] text-ink-2"><Trans>Invio non riuscito. Riprova.</Trans></p> : null}
        </div>
      )}
    </HighlightNote>
  );
};
